/**
 * UI-3a Task 4 — CampaignContactsTab.
 *
 * The "Contacts" tab of the Campaign drawer (spec §3.5): a 5-step CSV-import
 * wizard for bulk-loading contacts into a campaign.
 *
 * State machine
 * -------------
 *   drop  →  map  →  preview  →  committing  →  done
 *
 * - drop       : pick / drag-drop a CSV file.
 * - map        : detect the header row and map each CSV column to a target
 *                contact field (or "do not import").
 * - preview    : render the first 10 mapped rows in a small table.
 * - committing : POST the raw CSV body to the backend.
 * - done       : show imported / failed counts; offer a failed-rows download.
 *
 * Why useState (not ZodForm)
 * --------------------------
 * The wizard's step / file / mapping are genuine sequential UI state, not a
 * validated form. Plan §0.H's ZodForm rule targets forms & drawers, not a
 * file-import wizard, so plain useState is correct here.
 *
 * Backend contract
 * ----------------
 * Route: POST /api/campaigns/:campaignId/contacts/import
 *   (apps/api/src/routes/contacts-import.ts — mounted at the Fastify root;
 *    the web app reaches the API under the `/api` prefix, as the shared
 *    `api` client does).
 *
 * The shared `api` client only sends JSON bodies, so a raw `fetch` is used
 * here specifically because the body is `text/csv` (matching the plan).
 *
 * Success response (HTTP 201): `{ imported: number, campaignId: string }`.
 * The route does not currently report per-row failures, but this component
 * defensively reads optional `failed` / `errors` fields so it stays correct
 * if the backend starts returning them.
 */
import { useEffect, useRef, useState, type DragEvent, type JSX } from 'react'
import { parseCsv, applyMapping, type ParsedCsv } from '../../../utils/csv-import.js'
import { useAuthStore } from '../../../store/authStore.js'
import Select from '../../ui/Select.js'

// --- Public interface -----------------------------------------------------

export interface CampaignContactsTabProps {
  campaignId: string
  /**
   * Current contact count for the campaign. Optional: when a number is
   * provided it is displayed (spec §3.4, criterion 11 — "shows current
   * contact count"); when undefined nothing is rendered for the count.
   */
  contactCount?: number
  /** Called after a successful commit so the parent can refresh its count. */
  onImported: () => void
}

// --- Wizard state ---------------------------------------------------------

type Step = 'drop' | 'map' | 'preview' | 'committing' | 'done'

/** Target contact fields accepted by the backend import route. */
const SKIP = '' as const
const TARGET_FIELDS = [
  { value: 'phone', label: 'phone (required)' },
  { value: 'firstName', label: 'firstName' },
  { value: 'lastName', label: 'lastName' },
  { value: 'timezone', label: 'timezone' },
  { value: 'consent', label: 'consent (opt-in flag)' },
] as const

/** Preview cap: parseCsv only returns this many rows; the true count may be higher. */
const PREVIEW_CAP = 10_000

interface ImportError {
  row?: number
  phone?: string
  message?: string
}

/** Backend response — `imported` is always present; the rest are optional. */
interface ImportResult {
  imported: number
  failed?: number
  errors?: ImportError[]
  consent?: {
    attested: boolean
    granted: number
    suppressedOptedOut: number
    pending: number
  }
}

// --- Helpers --------------------------------------------------------------

/**
 * Auto-map a CSV header to a target field when the (lower-cased) name matches
 * a known field, so the common "phone,firstName,lastName" file needs no
 * manual mapping.
 */
function autoTarget(header: string): string {
  const norm = header.trim().toLowerCase()
  const match = TARGET_FIELDS.find((f) => f.value.toLowerCase() === norm)
  return match ? match.value : SKIP
}

/** Assemble a CSV Blob from per-row import errors. */
function failedRowsCsv(errors: ImportError[]): Blob {
  const head = 'row,phone,error'
  const escape = (v: string): string =>
    /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
  const lines = errors.map((e) =>
    [String(e.row ?? ''), escape(e.phone ?? ''), escape(e.message ?? '')].join(','),
  )
  return new Blob([[head, ...lines].join('\n')], { type: 'text/csv' })
}

// --- Component ------------------------------------------------------------

export function CampaignContactsTab({
  campaignId,
  contactCount,
  onImported,
}: CampaignContactsTabProps): JSX.Element {
  const [step, setStep] = useState<Step>('drop')
  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  /** csvHeader → target field (or SKIP). */
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Consent attestation state
  const [consentAttested, setConsentAttested] = useState(false)
  const [consentSource, setConsentSource] = useState('')
  const [consentChannel, setConsentChannel] = useState<'VOICE' | 'SMS' | 'EMAIL' | 'WHATSAPP'>('VOICE')
  const [consentAllRows, setConsentAllRows] = useState(true)

  const fileInputRef = useRef<HTMLInputElement>(null)
  /** Synchronous double-submit guard: set before any `await` in `commit()`. */
  const inFlightRef = useRef(false)
  /** False once unmounted — guards `setState` after an awaited boundary. */
  const mountedRef = useRef(true)
  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  // --- drop step ----------------------------------------------------------

  async function acceptFile(f: File): Promise<void> {
    setError(null)
    let text: string
    try {
      text = await f.text()
    } catch {
      if (!mountedRef.current) return
      setError('Could not read the file. Please try a different file.')
      return
    }
    if (!mountedRef.current) return
    const p = parseCsv(text)
    if (p.headers.length === 0) {
      setError('The file is empty or has no header row.')
      return
    }
    setFile(f)
    setParsed(p)
    // Seed the mapping by auto-detecting known field names.
    const seed: Record<string, string> = {}
    for (const h of p.headers) seed[h] = autoTarget(h)
    setMapping(seed)
    setStep('map')
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files?.[0]
    if (f) void acceptFile(f)
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault()
    const f = e.dataTransfer.files?.[0]
    if (f) void acceptFile(f)
  }

  function onDragOver(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault()
  }

  // --- map step -----------------------------------------------------------

  const phoneMapped = Object.values(mapping).includes('phone')
  const oversize = (parsed?.totalRows ?? 0) > PREVIEW_CAP

  function setColumnTarget(header: string, target: string): void {
    setMapping((prev) => ({ ...prev, [header]: target }))
  }

  // --- preview step -------------------------------------------------------

  const previewRows =
    parsed != null ? applyMapping(parsed, mapping).slice(0, 10) : []
  const mappedTargets = parsed
    ? parsed.headers.map((h) => mapping[h]).filter((t): t is string => Boolean(t))
    : []

  // --- committing step ----------------------------------------------------

  async function commit(): Promise<void> {
    if (!file) return
    // Synchronous re-entry guard: closes the race window between the first
    // click and the `committing` re-render that unmounts the Import button.
    if (inFlightRef.current) return
    if (consentAttested && consentSource.trim().length < 3) {
      setError('Consent source is required (at least 3 characters) when attesting consent.')
      return
    }
    inFlightRef.current = true
    setStep('committing')
    setError(null)
    try {
      const text = await file.text()
      const params = new URLSearchParams()
      if (consentAttested) {
        params.set('consentAttested', 'true')
        params.set('consentSource', consentSource.trim())
        params.set('consentChannel', consentChannel)
        if (consentAllRows) params.set('consentAllRows', 'true')
      }
      const qs = params.toString() ? `?${params.toString()}` : ''
      const res = await fetch(`/api/campaigns/${campaignId}/contacts/import${qs}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/csv',
          'X-CSRF-Token': useAuthStore.getState().csrfToken ?? '',
        },
        credentials: 'include',
        body: text,
      })
      // A non-JSON error body (HTML 500, empty body) must not mask the real
      // failure — fall back to a status-based message.
      const body = (await res.json().catch(() => ({}))) as ImportResult & {
        message?: string
      }
      if (!mountedRef.current) return
      if (!res.ok) {
        throw new Error(body.message ?? `Import failed (${res.status})`)
      }
      setResult(body)
      setStep('done')
      onImported()
    } catch (err) {
      if (!mountedRef.current) return
      setError((err as Error).message)
      setStep('preview')
    } finally {
      inFlightRef.current = false
    }
  }

  // --- done step ----------------------------------------------------------

  function downloadFailedRows(): void {
    if (!result?.errors?.length) return
    const blob = failedRowsCsv(result.errors)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'failed-rows.csv'
    a.rel = 'noopener'
    a.click()
    // Defer revocation so it cannot race the browser starting the download.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  function reset(): void {
    setStep('drop')
    setFile(null)
    setParsed(null)
    setMapping({})
    setResult(null)
    setError(null)
    setConsentAttested(false)
    setConsentSource('')
    setConsentChannel('VOICE')
    setConsentAllRows(true)
  }

  // --- render -------------------------------------------------------------

  /** "Current contacts: N" line — only when a count was supplied. */
  const countLine =
    typeof contactCount === 'number' ? (
      <p className="text-sm text-gray-600">
        Current contacts: {contactCount.toLocaleString()}
      </p>
    ) : null

  return (
    <div className="flex flex-col gap-4">
      {error != null && (
        <p role="alert" className="text-red-600 text-sm">
          {error}
        </p>
      )}

      {step === 'drop' && countLine}

      {step === 'drop' && (
        <div
          aria-label="Drop CSV file here"
          onDrop={onDrop}
          onDragOver={onDragOver}
          className="flex flex-col items-center gap-3 rounded border-2 border-dashed border-gray-300 p-8 text-center"
        >
          <p className="text-sm text-gray-600">
            Drag &amp; drop a CSV file here, or
          </p>
          <button
            type="button"
            className="rounded bg-[#16273F] px-3 py-1.5 text-sm text-white"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose file
          </button>
          <input
            ref={fileInputRef}
            data-testid="csv-file-input"
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            aria-label="Choose CSV file"
            onChange={onInputChange}
          />
        </div>
      )}

      {step === 'map' && parsed != null && (
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-semibold">Map columns</h3>
          {oversize && (
            <p role="alert" className="text-amber-600 text-sm">
              Large file detected — {parsed.totalRows.toLocaleString()} rows. Only
              the first {PREVIEW_CAP.toLocaleString()} are previewed; the full
              file is still uploaded on import.
            </p>
          )}
          <p className="text-sm text-gray-600">
            Choose a target contact field for each column. {`"phone"`} is
            required.
          </p>
          <ul className="flex flex-col gap-2">
            {parsed.headers.map((header, idx) => {
              const selectId = `map-col-${idx}`
              return (
                <li key={`${header}-${idx}`} className="flex items-center gap-3">
                  <label htmlFor={selectId} className="w-40 truncate text-sm">
                    {header}
                  </label>
                                    <Select
                    ariaLabel={`Map column "${header}"`}
                    size="sm"
                    value={mapping[header] ?? SKIP}
                    onChange={(v) => setColumnTarget(header, v)}
                  >
                    <option value={SKIP}>— do not import —</option>
                    {TARGET_FIELDS.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.value}
                      </option>
                    ))}
                                    </Select>
                </li>
              )
            })}
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm"
              onClick={reset}
            >
              Back
            </button>
            <button
              type="button"
              className="rounded bg-[#16273F] px-3 py-1.5 text-sm text-white disabled:opacity-50"
              disabled={!phoneMapped}
              onClick={() => setStep('preview')}
            >
              Preview
            </button>
          </div>
          {!phoneMapped && (
            <p className="text-sm text-gray-500">
              Map a column to {`"phone"`} to continue.
            </p>
          )}
        </div>
      )}

      {step === 'preview' && parsed != null && (
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-semibold">Preview</h3>
          <p className="text-sm text-gray-600">
            Showing the first {previewRows.length} of{' '}
            {parsed.totalRows.toLocaleString()} rows.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  {mappedTargets.map((t) => (
                    <th
                      key={t}
                      className="border-b border-gray-300 px-2 py-1 text-left font-medium"
                    >
                      {t}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {mappedTargets.map((t) => (
                      <td key={t} className="border-b border-gray-100 px-2 py-1">
                        {row[t] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Consent Attestation Card */}
          <div className="rounded border border-gray-200 bg-gray-50 p-4 flex flex-col gap-3" data-testid="consent-attestation-card">
            <div className="flex items-start gap-2">
              <input
                id="consent-attested"
                type="checkbox"
                checked={consentAttested}
                onChange={(e) => setConsentAttested(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="consent-attested-checkbox"
              />
              <label htmlFor="consent-attested" className="text-sm font-medium text-gray-800 cursor-pointer">
                I attest that these contacts have given consent to be contacted (TCPA compliance)
              </label>
            </div>

            {consentAttested && (
              <div className="flex flex-col gap-3 pl-6 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-600">
                  Contacts will be recorded with verifiable consent. Contacts who previously opted out (via STOP reply or DNC) will be automatically suppressed.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="consent-channel" className="block text-xs font-medium text-gray-700 mb-1">
                      Channel *
                    </label>
                    <Select
                      value={consentChannel}
                      onChange={(v) => setConsentChannel(v as typeof consentChannel)}
                      ariaLabel="Consent channel"
                      size="sm"
                    >
                      <option value="VOICE">VOICE</option>
                      <option value="SMS">SMS</option>
                      <option value="EMAIL">EMAIL</option>
                      <option value="WHATSAPP">WHATSAPP</option>
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="consent-source" className="block text-xs font-medium text-gray-700 mb-1">
                      Consent Source *
                    </label>
                    <input
                      id="consent-source"
                      type="text"
                      value={consentSource}
                      onChange={(e) => setConsentSource(e.target.value)}
                      placeholder="e.g. Web form signup 2026-10"
                      className="w-full rounded border border-gray-300 px-2 py-1 text-sm bg-white"
                      data-testid="consent-source-input"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="consent-all-rows"
                    type="checkbox"
                    checked={consentAllRows}
                    onChange={(e) => setConsentAllRows(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    data-testid="consent-all-rows-checkbox"
                  />
                  <label htmlFor="consent-all-rows" className="text-xs text-gray-700 cursor-pointer">
                    Apply consent to all rows in file {Object.values(mapping).includes('consent') ? '(override individual row consent values)' : ''}
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm"
              onClick={() => setStep('map')}
            >
              Back
            </button>
            <button
              type="button"
              className="rounded bg-[#16273F] px-3 py-1.5 text-sm text-white"
              onClick={() => void commit()}
            >
              Import
            </button>
          </div>
        </div>
      )}

      {step === 'committing' && (
        <p className="text-sm text-gray-600">Importing contacts…</p>
      )}

      {step === 'done' && result != null && (
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-semibold">Import complete</h3>
          {countLine}
          <p className="text-sm text-green-700">
            {result.imported} {result.imported === 1 ? 'contact' : 'contacts'}{' '}
            imported.
          </p>
          {result.consent?.attested && (
            <div className="rounded border border-green-200 bg-green-50 p-3 text-sm space-y-1" data-testid="consent-result-summary">
              <p className="font-semibold text-green-900">Consent Summary</p>
              <p className="text-green-800">
                {result.consent.granted} {result.consent.granted === 1 ? 'contact' : 'contacts'} granted consent ({consentChannel}).
              </p>
              {result.consent.suppressedOptedOut > 0 && (
                <p className="text-amber-800 font-medium">
                  {result.consent.suppressedOptedOut} {result.consent.suppressedOptedOut === 1 ? 'contact was' : 'contacts were'} suppressed (previously opted out via STOP reply or DNC).
                </p>
              )}
              {result.consent.pending > 0 && (
                <p className="text-gray-700">
                  {result.consent.pending} {result.consent.pending === 1 ? 'contact remains' : 'contacts remain'} pending consent.
                </p>
              )}
            </div>
          )}
          {(result.failed ?? 0) > 0 && (
            <p className="text-sm text-red-600">
              {result.failed} {result.failed === 1 ? 'row' : 'rows'} failed.
            </p>
          )}
          <div className="flex gap-2">
            {result.errors != null && result.errors.length > 0 && (
              <button
                type="button"
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                onClick={downloadFailedRows}
              >
                Download failed rows
              </button>
            )}
            <button
              type="button"
              className="rounded bg-[#16273F] px-3 py-1.5 text-sm text-white"
              onClick={reset}
            >
              Import another file
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
